import React, { useState, useEffect } from 'react';
import { adminUserService } from '../../services/adminService';

const FILTER_ROLES = ['all', 'buyer', 'seller', 'inspector', 'dealer', 'driver', 'admin'];
const CREATABLE_ROLES = ['inspector', 'dealer', 'driver', 'admin'];

const ROLE_CONFIG = {
  buyer:     { color: '#28a745', label: 'Buyer' },
  seller:    { color: '#ffc107', label: 'Seller' },
  inspector: { color: '#17a2b8', label: 'Inspector' },
  dealer:    { color: '#6f42c1', label: 'Dealer' },
  driver:    { color: '#fd7e14', label: 'Driver' },
  admin:     { color: '#003566', label: 'Admin' },
};

const EMPTY_FORM = {
  name: '', email: '', phone: '', address: '',
  role: 'inspector', status: 'active', password: ''
};

// ── Password Input ────────────────────────────────────────────────────
const PasswordInput = ({ value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', marginTop: 4 }}>
      <input
        style={{ ...inp, paddingRight: 42, marginTop: 0 }}
        name="password"
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        style={{
          position: 'absolute', right: 10, top: '50%',
          transform: 'translateY(-50%)',
          background: 'none', border: 'none',
          cursor: 'pointer', color: '#888', fontSize: 16, padding: 2, lineHeight: 1,
        }}
        title={show ? 'Hide password' : 'Show password'}
      >
        {show ? '🙈' : '👁️'}
      </button>
    </div>
  );
};

// ── Dealer Profile Modal ──────────────────────────────────────────────
const DealerProfileModal = ({ user, onClose }) => {
  const dp = user.dealer_profile || {};
  return (
    <div style={overlay}>
      <div style={{ ...modal, maxWidth: 520 }}>
        <div style={modalHeader}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#003566' }}>
            🏢 Dealer Profile — {user.name || user.email || `#${user.id}`}
          </h2>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>
        <div style={{ ...modalBody, gap: 0 }}>

          {/* Basic info */}
          <Section title="Basic Info">
            <Row label="Full Name"  value={user.name}    />
            <Row label="Email"      value={user.email}   />
            <Row label="Phone"      value={user.phone ? `+91 ${user.phone}` : null}
                 extra={user.phone_verified
                   ? <Badge color="#28a745">✓ Verified</Badge>
                   : <Badge color="#ffc107">Unverified</Badge>}
            />
            <Row label="Address"    value={user.address} />
            <Row label="Status"     value={user.status}  />
          </Section>

          {/* Dealer business info */}
          <Section title="Business Details">
            <Row label="Shop / Business Name"  value={dp.shop_name}   />
            <Row label="GST Number"            value={dp.gst_number || 'Not provided'} />
            <Row label="Verification Status"   value={dp.verification_status}
                 extra={dp.verification_status && (
                   <Badge color={
                     dp.verification_status === 'verified' ? '#28a745'
                     : dp.verification_status === 'rejected' ? '#dc3545' : '#ffc107'
                   }>
                     {dp.verification_status.charAt(0).toUpperCase() + dp.verification_status.slice(1)}
                   </Badge>
                 )}
            />
          </Section>

          {/* Documents */}
          <Section title="Documents">
            {/* Aadhaar */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 6 }}>
                Aadhaar Card
              </div>
              {dp.aadhaar_image_url ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img
                    src={dp.aadhaar_image_url}
                    alt="Aadhaar"
                    style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <a
                    href={dp.aadhaar_image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#17a2b8', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}
                  >
                    📄 View Aadhaar
                  </a>
                </div>
              ) : (
                <span style={{ fontSize: 13, color: '#999' }}>Not uploaded</span>
              )}
            </div>

            {/* Other attached documents */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 6 }}>
                Other Documents
              </div>
              {user.documents?.length > 0 ? (
                user.documents.map(doc => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'block', color: '#17a2b8', fontSize: 13, marginBottom: 4, textDecoration: 'none' }}
                  >
                    📄 {doc.filename}
                  </a>
                ))
              ) : (
                <span style={{ fontSize: 13, color: '#999' }}>No documents uploaded</span>
              )}
            </div>
          </Section>
        </div>
        <div style={modalFooter}>
          <button onClick={onClose} style={btnSecondary}>Close</button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: '#003566',
      textTransform: 'uppercase', letterSpacing: 1,
      borderBottom: '1px solid #eee', paddingBottom: 4, marginBottom: 10
    }}>
      {title}
    </div>
    {children}
  </div>
);

const Row = ({ label, value, extra }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f5f5f5' }}>
    <span style={{ fontSize: 12, color: '#888', minWidth: 140 }}>{label}</span>
    <span style={{ fontSize: 13, color: '#333', fontWeight: 500, textAlign: 'right', display: 'flex', alignItems: 'center', gap: 6 }}>
      {value || <span style={{ color: '#ccc' }}>—</span>}
      {extra}
    </span>
  </div>
);

const Badge = ({ color, children }) => (
  <span style={{
    background: `${color}18`, color,
    border: `1px solid ${color}40`,
    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600
  }}>
    {children}
  </span>
);

// ── User Create/Edit Modal ────────────────────────────────────────────
const UserModal = ({ title, form, onChange, onSubmit, onClose, isEdit, error, busy }) => (
  <div style={overlay}>
    <div style={modal}>
      <div style={modalHeader}>
        <h2 style={{ margin: 0, fontSize: 20, color: '#003566' }}>{title}</h2>
        <button onClick={onClose} style={closeBtn}>✕</button>
      </div>

      <div style={modalBody}>
        {error && (
          <div style={{
            background: '#dc354515', color: '#dc3545',
            border: '1px solid #dc354530', borderRadius: 8,
            padding: '10px 14px', fontSize: 13, marginBottom: 4
          }}>
            ⚠️ {error}
          </div>
        )}

        <label style={lbl}>Full Name</label>
        <input style={inp} name="name" placeholder="Full name"
          value={form.name} onChange={onChange} />

        <label style={lbl}>Email</label>
        <input style={inp} name="email" type="email" placeholder="Email address (required)"
          value={form.email} onChange={onChange} />

        <label style={lbl}>Phone (10 digits)</label>
        <input style={inp} name="phone" placeholder="10-digit phone number"
          value={form.phone} onChange={onChange} maxLength={10} />

        <label style={lbl}>Address</label>
        <input style={inp} name="address" placeholder="Address"
          value={form.address} onChange={onChange} />

        <label style={lbl}>Role</label>
        <select style={inp} name="role" value={form.role} onChange={onChange}>
          {CREATABLE_ROLES.map(r => (
            <option key={r} value={r}>{ROLE_CONFIG[r]?.label ?? r}</option>
          ))}
        </select>

        <label style={lbl}>Status</label>
        <select style={inp} name="status" value={form.status} onChange={onChange}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="blocked">Blocked</option>
        </select>

        <label style={lbl}>
          Password{' '}
          {isEdit && <span style={{ color: '#999', fontWeight: 400, fontSize: 12 }}>(leave blank to keep current)</span>}
        </label>
        <PasswordInput
          value={form.password}
          onChange={onChange}
          placeholder={isEdit ? 'Leave blank to keep current' : 'Set a strong password'}
        />
      </div>

      <div style={modalFooter}>
        <button onClick={onClose} style={btnSecondary} disabled={busy}>Cancel</button>
        <button onClick={onSubmit} style={{ ...btnPrimary, opacity: busy ? 0.7 : 1 }} disabled={busy}>
          {busy ? 'Saving…' : isEdit ? 'Update User' : 'Create User'}
        </button>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────
const UsersManagement = () => {
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState('all');
  const [searchTerm, setSearch]         = useState('');
  const [showCreate, setCreate]         = useState(false);
  const [editUser, setEditUser]         = useState(null);
  const [viewDealerUser, setViewDealer] = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [formError, setFormError]       = useState('');
  const [formBusy, setFormBusy]         = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await adminUserService.getAllUsers();
      setUsers(res.users || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setCreate(true);
  };

  const openEdit = (user) => {
    setForm({
      name:     user.name    || '',
      email:    user.email   || '',
      phone:    user.phone   || '',
      address:  user.address || '',
      role:     CREATABLE_ROLES.includes(user.role) ? user.role : 'inspector',
      status:   user.status  || 'active',
      password: ''
    });
    setFormError('');
    setEditUser(user);
  };

  const closeModals = () => {
    setCreate(false);
    setEditUser(null);
    setViewDealer(null);
    setFormError('');
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleCreate = async () => {
    setFormBusy(true);
    setFormError('');
    try {
      await adminUserService.createUser(form);
      closeModals();
      loadUsers();
    } catch (e) {
      const msgs = e.response?.data?.errors || [e.response?.data?.error || e.message];
      setFormError(Array.isArray(msgs) ? msgs.join(', ') : msgs);
    } finally {
      setFormBusy(false);
    }
  };

  const handleUpdate = async () => {
    setFormBusy(true);
    setFormError('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await adminUserService.updateUser(editUser.id, payload);
      closeModals();
      loadUsers();
    } catch (e) {
      const msgs = e.response?.data?.errors || [e.response?.data?.error || e.message];
      setFormError(Array.isArray(msgs) ? msgs.join(', ') : msgs);
    } finally {
      setFormBusy(false);
    }
  };

  const handleActivate   = async (id) => { try { await adminUserService.activateUser(id);   loadUsers(); } catch (e) { console.error(e); } };
  const handleDeactivate = async (id) => { try { await adminUserService.deactivateUser(id); loadUsers(); } catch (e) { console.error(e); } };
  const handleDelete     = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try { await adminUserService.deleteUser(id); loadUsers(); }
    catch (e) { alert(`Delete failed: ${e.response?.data?.error || e.message}`); }
  };

  const getUserType = (user) => {
    if (user.role) return user.role;
    if (user.has_sell_requests || user.sell_requests?.length > 0) return 'seller';
    return 'buyer';
  };

  const filteredUsers = users.filter((user) => {
    if (user.status === 'inactive') return false;
    const type = getUserType(user);
    const matchesFilter =
      filter === 'all' || type === filter;
    const matchesSearch =
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const countByRole = (role) =>
    role === 'all' ? users.length : users.filter(u => getUserType(u) === role).length;

  const getRoleBadge = (user) => {
    const role = getUserType(user);
    const cfg  = ROLE_CONFIG[role] || { color: '#6c757d', label: role };
    return (
      <span style={{
        background: `${cfg.color}18`, color: cfg.color,
        border: `1px solid ${cfg.color}40`,
        padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600
      }}>
        {cfg.label}
      </span>
    );
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><p>Loading users…</p></div>;

  return (
    <div className="users-management">
      {/* Dealer profile detail modal */}
      {viewDealerUser && (
        <DealerProfileModal user={viewDealerUser} onClose={closeModals} />
      )}

      {showCreate && (
        <UserModal
          title="Create New User"
          form={form} onChange={handleChange} onSubmit={handleCreate}
          onClose={closeModals} isEdit={false} error={formError} busy={formBusy}
        />
      )}
      {editUser && (
        <UserModal
          title={`Edit User — ${editUser.name || editUser.email || `#${editUser.id}`}`}
          form={form} onChange={handleChange} onSubmit={handleUpdate}
          onClose={closeModals} isEdit error={formError} busy={formBusy}
        />
      )}

      <div className="management-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Users Management</h1>
          <p>Manage all platform users and permissions</p>
        </div>
        <button onClick={openCreate} style={btnPrimary}>+ Create User</button>
      </div>

      <div className="management-filters">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-tabs">
          {FILTER_ROLES.map(role => (
            <button key={role}
              className={`filter-tab ${filter === role ? 'active' : ''}`}
              onClick={() => setFilter(role)}
            >
              {role === 'all' ? 'All Users' : ROLE_CONFIG[role]?.label ?? role}
              <span className="filter-count">{countByRole(role)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="management-table">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Address / Business</th>
              <th>Documents</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => {
              const isDealer = getUserType(user) === 'dealer';
              const dp = user.dealer_profile;

              return (
                <tr key={user.id}>
                  {/* User column */}
                  <td>
                    <div className="user-details">
                      <div className="user-avatar"
                        style={{ background: ROLE_CONFIG[getUserType(user)]?.color || '#003566' }}>
                        {(user.name || user.email || 'U')[0].toUpperCase()}
                      </div>
                      <div className="user-info">
                        <h4>{user.name || 'N/A'}</h4>
                        <p>ID: {user.id}</p>
                      </div>
                    </div>
                  </td>

                  {/* Email */}
                  <td>{user.email || 'N/A'}</td>

                  {/* Phone — show verified badge for dealers */}
                  <td>
                    {user.phone || 'N/A'}
                    {isDealer && user.phone_verified && (
                      <span style={{
                        display: 'block', fontSize: 10, color: '#28a745',
                        fontWeight: 600, marginTop: 2
                      }}>✓ Verified</span>
                    )}
                  </td>

                  {/* Address — show shop name for dealers */}
                  <td>
                    {isDealer && dp?.shop_name ? (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#6f42c1' }}>
                          🏢 {dp.shop_name}
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                          {user.address || 'No address'}
                        </div>
                      </div>
                    ) : (
                      user.address || 'N/A'
                    )}
                  </td>

                  {/* Documents — for dealers show aadhaar + GST summary + docs button */}
                  <td>
                    {isDealer ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* GST */}
                        <div style={{ fontSize: 11 }}>
                          <span style={{ color: '#888' }}>GST: </span>
                          <span style={{ color: dp?.gst_number ? '#333' : '#ccc', fontWeight: dp?.gst_number ? 600 : 400 }}>
                            {dp?.gst_number || 'Not provided'}
                          </span>
                        </div>

                        {/* Verification status pill */}
                        {dp?.verification_status && (
                          <span style={{
                            fontSize: 10, fontWeight: 600,
                            padding: '1px 7px', borderRadius: 8, width: 'fit-content',
                            background: dp.verification_status === 'verified' ? '#28a74520'
                                      : dp.verification_status === 'rejected' ? '#dc354520' : '#ffc10720',
                            color: dp.verification_status === 'verified' ? '#28a745'
                                 : dp.verification_status === 'rejected' ? '#dc3545' : '#856404',
                          }}>
                            {dp.verification_status.charAt(0).toUpperCase() + dp.verification_status.slice(1)}
                          </span>
                        )}

                        {/* View full profile button */}
                        <button
                          onClick={() => setViewDealer(user)}
                          style={{
                            fontSize: 11, fontWeight: 600, color: '#6f42c1',
                            background: '#6f42c115', border: '1px solid #6f42c140',
                            borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                            width: 'fit-content', marginTop: 2
                          }}
                        >
                          👁 View Details
                        </button>
                      </div>
                    ) : (
                      user.documents?.length > 0
                        ? user.documents.map(doc => (
                            <a key={doc.id} href={doc.url} target="_blank" rel="noopener noreferrer"
                              className="document-link">📄 {doc.filename}</a>
                          ))
                        : <span>N/A</span>
                    )}
                  </td>

                  {/* Role badge */}
                  <td>{getRoleBadge(user)}</td>

                  {/* Joined */}
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>

                  {/* Status */}
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 12,
                      background: user.status === 'active' ? '#28a74520'
                                : user.status === 'blocked' ? '#dc354520' : '#ffc10720',
                      color: user.status === 'active' ? '#28a745'
                           : user.status === 'blocked' ? '#dc3545' : '#856404',
                    }}>
                      {user.status || 'N/A'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="action-buttons">
                      <button
                        style={{ ...actionBtn, background: '#17a2b820', color: '#17a2b8', border: '1px solid #17a2b840' }}
                        onClick={() => openEdit(user)} title="Edit user"
                      >✏️</button>
                      {getUserType(user) !== 'admin' && (
                        <>
                          <button className="btn-approve" onClick={() => handleActivate(user.id)}   title="Activate">✓</button>
                          <button className="btn-reject"  onClick={() => handleDeactivate(user.id)} title="Deactivate">✕</button>
                          <button className="btn-delete"  onClick={() => handleDelete(user.id)}     title="Delete">🗑</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="no-results"><p>No users found</p></div>
        )}
      </div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────
const overlay     = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
const modal       = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' };
const modalHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #eee', background: '#f8f9ff' };
const modalBody   = { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '65vh', overflowY: 'auto' };
const modalFooter = { padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 12 };
const lbl         = { fontSize: 13, fontWeight: 600, color: '#555', marginTop: 10 };
const inp         = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginTop: 4 };
const btnPrimary  = { background: '#ffd60a', color: '#003566', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 };
const btnSecondary= { background: '#f1f3f5', color: '#555', border: '1px solid #ddd', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 };
const closeBtn    = { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#666', lineHeight: 1 };
const actionBtn   = { padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 14 };

export default UsersManagement;